import React, { useEffect, useRef, useState } from "react";
import styles from "./Findings.module.scss";
import Table from "../../components/Table/Table";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import PageLoader from "../../components/Loader/PageLoader";
import classNames from "classnames";
import Input from "../../components/Input/Input";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import MultiSelect from "../../components/MultiSelect/MultiSelect";
import Select from "../../components/Select/Select";
import {
  formatDateSlash,
  getLinkedData,
  handleExport,
} from "../../utils/helpers";
import { showModal } from "../../redux/modalStore";
import { useDispatch, useSelector } from "react-redux";
import { FINDINGS_LOADER } from "../../constants/loader";
import { setLinkedData } from "../../redux/organizationStore";
import Button from "../../components/Button/Button";
import DateRangeFilter from "../../components/DateRangeFilter/DateFIlterRange";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import Swal from "sweetalert2";
import BulkEditModal from "../../modals/BulkEditModal/BulkEditModal";
import {
  clearResetSelection,
  resetSelection,
} from "../../redux/rowSelectionStore";
import ConfirmationPopup from "../../modals/ConfirmationPopup";
import ArchiveIllustration from "../../icons/archive-illustration.svg";
import { hideOverlay, showOverlay } from "../../redux/overlayLoaderStore";
import { setMeMode } from "../../redux/filterStore";
import { useTranslation } from "react-i18next";
import LinkImprovementModal from "../../modals/LinkImprovementModal";
import FindingsModal from "../../modals/FindingsModal";
import {
  AlertCircle,
  Archive,
  Edit2,
  FileText,
  Plus,
  TrendingUp,
} from "lucide-react";

const Findings = () => {
  const { t } = useTranslation();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const sessionToken = localStorage.getItem("session_token");
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedFindings, setSelectedFindings] = useState([]);
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allFindingsSearchQuery, setAllFindingsSearchQuery] = useState("");
  const [allFindingsSeverity, setAllFindingsSeverity] = useState("");
  const [allFindingsStatus, setAllFindingsStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState("");
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [actionToast, setActionToast] = useState(null);
  const actionToastTimerRef = useRef(null);

  useFetchOrganizationData();

  const loadingContexts = useSelector((state) => state.loader.loadingContexts);
  const { meModeActive } = useSelector((state) => state.filters);
  const { findingsData, improvementsData, data } = useSelector(
    (state) => state.organization,
  );
  const currentUser = useSelector((state) => state.user.email);
  const resetRowSelection = useSelector(
    (state) => state.rowSelection.resetSelection,
  );

  console.log(findingsData);

  useEffect(() => {
    return () => {
      if (actionToastTimerRef.current) {
        window.clearTimeout(actionToastTimerRef.current);
        actionToastTimerRef.current = null;
      }
    };
  }, []);

  const isFindingsDataLoading = loadingContexts[FINDINGS_LOADER];

  const findingsArray = Object.values(findingsData || {});
  const activeTab = searchParams.get("tab") === "all" ? "all" : "overview";

  // UseEffect for enabling/disabling me mode when navigating through kanban columns links
  useEffect(() => {
    if (location.state?.fromKanban) {
      dispatch(setMeMode(true));
    }

    return () => {
      if (location.state?.fromKanban) {
        dispatch(setMeMode(false));
      }
    };
  }, [dispatch, location.state?.fromKanban]);

  useEffect(() => {
    const rawRisks = searchParams.getAll("risk");
    const risksFromUrl = rawRisks.every((risk) => !risk.includes(","))
      ? rawRisks
      : [];
    setSelectedRisks(risksFromUrl);
  }, [searchParams]);

  useEffect(() => {
    const rawStatuses = searchParams.getAll("status");
    const statusesFromUrl = rawStatuses.every((s) => !s.includes(","))
      ? rawStatuses
      : [];
    setSelectedStatuses(statusesFromUrl);
  }, [searchParams]);

  // Unique responsible options
  const uniqueResponsibles = findingsArray
    .flatMap((finding) =>
      Array.isArray(finding.responsible)
        ? finding.responsible
        : [finding.responsible],
    )
    .filter(Boolean)
    .filter((value, index, self) => self.indexOf(value) === index)
    .map((responsible, index) => ({ id: responsible, label: responsible }));

  // Unique Risk Levels Options
  const uniqueLevels = findingsArray
    .map((finding) => finding.risk?.level || "unknown") // Handle missing level
    .filter((value, index, self) => value && self.indexOf(value) === index)
    .map((level) => ({
      id: level.toLowerCase(), // Keep lowercase for filtering
      label: level.charAt(0).toUpperCase() + level.slice(1).toLowerCase(), // Format for display
    }));

  const handleLevelFilterChange = (newSelectedValues) => {
    setSelectedLevels(newSelectedValues);
  };

  // Status Options
  const uniqueStatuses = findingsArray
    .map((finding) => finding.status)
    .filter((value, index, self) => value && self.indexOf(value) === index)
    .map((status, index) => ({ id: status, label: status }));

  // Unique Risk Options
  const uniqueRisks = findingsArray
    .map((finding) => finding.risk?.risk_id || "No Risk ID") // Handle missing risk_id
    .filter((value, index, self) => value && self.indexOf(value) === index)
    .map((riskId) => ({ id: riskId, label: riskId }))
    .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically by label

  const handleResponsibleFilterChange = (newSelectedValues) => {
    setSelectedResponsibles(newSelectedValues);
  };

  const handleStatusFilterChange = (newSelectedValues) => {
    setSelectedStatuses(newSelectedValues);

    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams.delete("status");
    newSelectedValues.forEach((status) => {
      newSearchParams.append("status", status);
    });

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const handleDateRangeFilterChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleRiskFilterChange = (newSelectedValues) => {
    setSelectedRisks(newSelectedValues);

    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams.delete("risk");
    newSelectedValues.forEach((risk) => {
      newSearchParams.append("risk", risk);
    });

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const handleTabChange = (nextTab) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === "overview") {
      nextSearchParams.delete("tab");
    } else {
      nextSearchParams.set("tab", "all");
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleExportSelectedFindings = async () => {
    setLoading(true);
    setLoadingAction("export");

    await handleExport({
      selectedItems: selectedFindings,
      orgId,
      sessionToken,
      getKey: (item) => item.key,
      filePrefix: "Finding",
    });
    setLoading(false);
    setLoadingAction("");
  };

  const resetSelectionState = () => {
    setSelectedFindings([]);
    dispatch(resetSelection());
    window.setTimeout(() => dispatch(clearResetSelection()), 100);
  };

  const handleClearSelection = () => {
    resetSelectionState();
  };

  const handleBulkArchive = async () => {
    setShowConfirmationPopup(false);

    if (!selectedFindings.length) return;

    const payload = selectedFindings.map((project) => ({
      id: project.id,
      archived: true,
    }));

    const archivedKeys = selectedFindings.map((project) => project.key);

    setLoading(true);
    setLoadingAction("archive");
    dispatch(showOverlay());

    try {
      await axiosInstance.patch(`/nocodb/findings?org_id=${orgId}`, payload, {
        headers: {
          "Content-Type": "application/json",
          "session-token": sessionToken,
        },
      });

      // Dispatch updated organization data
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          false,
          false,
          false,
          false,
        ),
      );

      // Show success alert
      Swal.fire({
        toast: true,
        icon: "success",
        title: t("findings.swal.archiveSuccessTitle"),
        text: t("findings.swal.archiveSuccessText", {
          keys: archivedKeys.join(", "),
        }),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire({
        toast: true,
        icon: "error",
        title: t("findings.swal.archiveFailedTitle"),
        text: t("findings.swal.archiveFailedText"),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
      setLoadingAction("");
      dispatch(hideOverlay()); // Hide overlay

      resetSelectionState();
    }
  };

  const filteredData = findingsArray
    .filter((item) => !item.archived)
    .filter((item) => {
      const {
        responsible,
        accountable,
        consulted,
        informed,
        status,
        date: itemDate,
        risk,
        reference,
        reporter,
      } = item;
      const dueDate = new Date(itemDate);

      const formattedStartDate = dateRange[0];
      const formattedEndDate = dateRange[1];

      const searchQueryLower = searchQuery.toLowerCase();

      const filters = [
        selectedResponsibles.length === 0 ||
        (Array.isArray(responsible)
          ? responsible.some((r) => selectedResponsibles.includes(r))
          : selectedResponsibles.includes(responsible)),
        selectedStatuses.length === 0 || selectedStatuses.includes(status),
        selectedLevels.length === 0 ||
        selectedLevels.includes(risk?.level?.toLowerCase() || "unknown"),
        selectedRisks.length === 0 ||
        selectedRisks.includes(risk?.risk_id || "No Risk ID"),
        (!formattedStartDate && !formattedEndDate) ||
        (dueDate >= formattedStartDate && dueDate <= formattedEndDate),
        !searchQueryLower ||
        item.key?.toLowerCase().includes(searchQueryLower) ||
        item.description?.toLowerCase().includes(searchQueryLower) ||
        reference?.toLowerCase().includes(searchQueryLower) ||
        reporter?.toLowerCase().includes(searchQueryLower) ||
        (Array.isArray(responsible)
          ? responsible.some((r) =>
            r
              .toLowerCase()
              .normalize()
              .includes(searchQueryLower.normalize()),
          )
          : responsible
            ?.toLowerCase()
            .normalize()
            .includes(searchQueryLower.normalize())) ||
        status
          ?.toLowerCase()
          .normalize()
          .includes(searchQueryLower.normalize()) ||
        risk?.qms_id
          ?.toLowerCase()
          .normalize()
          .includes(searchQueryLower.normalize()) ||
        risk?.risk_id
          ?.toLowerCase()
          .normalize()
          .includes(searchQueryLower.normalize()) ||
        risk?.level
          ?.toLowerCase()
          .normalize()
          .includes(searchQueryLower.normalize()),
        !meModeActive ||
        [responsible, accountable, consulted, informed, reporter].some(
          (role) =>
            Array.isArray(role)
              ? role.includes(currentUser)
              : role === currentUser,
        ),
      ];

      return filters.every(Boolean);
    });

  const selectedFindingsCount = selectedFindings.length;
  const hasSelectedFindings = selectedFindingsCount > 0;
  const selectedFindingsLabel =
    selectedFindingsCount === 1
      ? t("findings.selectionBar.single", { count: selectedFindingsCount })
      : t("findings.selectionBar.multiple", { count: selectedFindingsCount });

  const handleRowClick = (row) => {
    const linkedData = getLinkedData(row.original, data);
    dispatch(setLinkedData(linkedData));
    navigate(`/${orgId}/findings/${row.original.key}${location.search}`, {
      replace: false,
    });
  };

  const handleFindingCardClick = (finding) => {
    const linkedData = getLinkedData(finding, data);

    dispatch(setLinkedData(linkedData));

    dispatch(
      showModal({
        name: "findings_modal",
        data: { findingKey: finding.key },
      }),
    );
  };

  const truncateText = (text, maxLength) => {
    if (text?.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text?.substring(0, maxLength)}
        <span className={styles.readMore}>... read more</span>
      </>
    );
  };

  const handleSelectedRowsChange = (selectedRows) => {
    setSelectedFindings(selectedRows);
  };

  const handleOpenNewImprovementModal = () => {
    dispatch(
      showModal({
        name: "new_improvement_modal",
        data: selectedFindings,
      }),
    );
  };

  const handleLinkedImprovementClick = (event, improvementKey) => {
    event.stopPropagation();
    if (!improvementKey) return;

    navigate(
      `/${orgId}/improvement-projects/${improvementKey}${location.search}`,
    );
  };

  const handleBulkEdit = () => {
    dispatch(
      showModal({
        name: "bulk_edit_modal",
        data: selectedFindings,
      }),
    );
  };

  const showActionToast = (toast) => {
    setActionToast(toast);
    if (actionToastTimerRef.current) {
      window.clearTimeout(actionToastTimerRef.current);
    }
    actionToastTimerRef.current = window.setTimeout(() => {
      setActionToast(null);
      actionToastTimerRef.current = null;
    }, 4000);
  };

  const handleBulkEditClick = () => {
    if (selectedFindings.length === 0) {
      showActionToast({
        title: t("findings.actionToast.noneSelectedTitle"),
        text: t("findings.actionToast.bulkEditNoneSelectedText"),
      });
      return;
    }
    if (selectedFindings.length < 2) {
      showActionToast({
        title: t("findings.actionToast.notEnoughSelectedTitle"),
        text: t("findings.actionToast.bulkEditMinSelectedText"),
      });
      return;
    }
    handleBulkEdit();
  };

  const handleArchiveClick = () => {
    if (selectedFindings.length === 0) {
      showActionToast({
        title: t("findings.actionToast.noneSelectedTitle"),
        text: t("findings.actionToast.archiveNoneSelectedText"),
      });
      return;
    }
    setShowConfirmationPopup(true);
  };

  const handleExportClick = () => {
    if (selectedFindings.length === 0) {
      showActionToast({
        title: t("findings.actionToast.noneSelectedTitle"),
        text: t("findings.actionToast.exportNoneSelectedText"),
      });
      return;
    }
    handleExportSelectedFindings();
  };

  const handleCreateImprovementClick = () => {
    if (selectedFindings.length === 0) {
      showActionToast({
        title: t("findings.actionToast.noneSelectedTitle"),
        text: t("findings.actionToast.createImprovementNoneSelectedText"),
      });
      return;
    }
    handleOpenNewImprovementModal();
  };

  const handleLinkImprovementClick = (event, finding) => {
    event.stopPropagation();
    if (!finding) return;

    dispatch(
      showModal({
        name: "link_improvement_modal",
        data: finding,
      }),
    );
  };

  const columns = [
    {
      accessorKey: "select",
      header: ({ table }) => (
        <div
          className={styles.customCheckboxContainer}
          onClick={(e) => {
            e.stopPropagation(); // Stop click event propagation
            table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected());
          }}
        >
          {table.getIsAllPageRowsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      cell: ({ row }) => (
        <div className={styles.customCheckboxContainer}>
          {row.getIsSelected() ? (
            <div className={styles.checked} />
          ) : (
            <div className={styles.unchecked} />
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "key",
      header: t("findings.table.key"),
      cell: (props) => (
        <span className={styles.keyPill}>{props.getValue()}</span>
      ),
      enableSorting: false,
      // sortingFn: "alphanumeric",
    },
    {
      accessorKey: "reference",
      header: t("findings.table.reference"),
      cell: (props) => <div>{props.getValue()}</div>,
      enableSorting: false,
    },
    {
      accessorKey: "risk.risk_id",
      header: t("findings.table.risk"),
      cell: (props) => {
        const riskId = props.row.original?.risk?.risk_id;
        return riskId ? (
          <span className={styles.riskPill}>{riskId}</span>
        ) : (
          <div>{t("findings.table.noRiskId")}</div>
        );
      },
      enableSorting: false,
      // sortingFn: (rowA, rowB, columnId) => {
      //   const a = (rowA.getValue(columnId) || "").split(/[\.-]/);
      //   const b = (rowB.getValue(columnId) || "").split(/[\.-]/);

      //   const maxLen = Math.max(a.length, b.length);
      //   for (let i = 0; i < maxLen; i++) {
      //     const partA = a[i] ?? "";
      //     const partB = b[i] ?? "";

      //     const numA = Number(partA);
      //     const numB = Number(partB);

      //     const isNumA = !isNaN(numA);
      //     const isNumB = !isNaN(numB);

      //     if (isNumA && isNumB) {
      //       if (numA !== numB) return numA - numB;
      //     } else {
      //       if (partA !== partB) return partA.localeCompare(partB);
      //     }
      //   }

      //   return 0;
      // },
    },
    {
      accessorKey: "risk.level",
      header: t("findings.table.level"),
      cell: (props) => {
        const level = props.row.original?.risk?.level || "unknown"; // Fallback if level is undefined
        const formattedLevel =
          level.charAt(0).toUpperCase() + level.slice(1).toLowerCase(); // Format to uppercase first letter
        return <div>{formattedLevel}</div>;
      },
      enableSorting: false,
    },
    {
      accessorKey: "description",
      header: t("findings.table.description"),
      cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
      enableSorting: false,
    },
    {
      accessorKey: "responsible",
      header: t("findings.table.responsible"),
      cell: (props) => {
        const responsible = props.row.original.responsible;
        const hasResponsible = Array.isArray(responsible)
          ? responsible.length > 0
          : Boolean(responsible);

        return (
          <div>{hasResponsible ? t("findings.table.assigned") : ""}</div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: t("findings.table.status"),
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = styles.openStatus;
            break;
          case "In Progress":
            statusClassName = styles.inProgressStatus;
            break;
          case "In Review":
            statusClassName = styles.inReviewStatus;
            break;
          case "Done":
            statusClassName = styles.resolvedStatus;
            break;
          default:
            break;
        }

        return (
          <div className={classNames(styles.statusCell, statusClassName)}>
            {status}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "date",
      header: t("findings.table.date"),
      cell: (props) => <div>{formatDateSlash(props.getValue())}</div>,
      enableSorting: false,
      // sortingFn: "datetime",
    },
    {
      accessorKey: "linked_improvements",
      header: t("findings.table.linkedImprovements"),
      cell: ({ row }) => {
        const linkedImprovements = row.original?.linked_improvements || [];
        const improvementEntries = linkedImprovements
          .map((improvement) => {
            const key =
              typeof improvement === "string" || typeof improvement === "number"
                ? improvement.toString()
                : improvement?.key;
            if (!key) return null;

            const improvementData =
              (typeof improvement === "object" && improvement) ||
              improvementsData?.[key];

            return {
              key,
              description:
                improvementData?.description ||
                improvementData?.name ||
                improvementData?.summary ||
                "",
            };
          })
          .filter(Boolean);

        if (!improvementEntries.length) {
          return (
            <div className={styles.linkedImprovementsCell}>
              <button
                type="button"
                className={styles.linkImprovementButton}
                onClick={(event) =>
                  handleLinkImprovementClick(event, row.original)
                }
              >
                <TrendingUp size={14} className={styles.improvementIcon} />
                {t("findings.table.linkImprovement")}
              </button>
            </div>
          );
        }

        const primaryImprovement =
          improvementEntries[improvementEntries.length - 1];
        const extraCount = improvementEntries.length - 1;
        const truncateDescription = (text, maxLength = 32) => {
          if (!text) return "";
          return text.length > maxLength
            ? `${text.substring(0, maxLength)}...`
            : text;
        };

        return (
          <div className={styles.linkedImprovementsCell}>
            <span
              className={styles.linkedImprovementPill}
              title={
                primaryImprovement.description || primaryImprovement.key || ""
              }
              onClick={(event) =>
                handleLinkedImprovementClick(event, primaryImprovement.key)
              }
            >
              <TrendingUp size={14} className={styles.improvementIcon} />
              <span className={styles.improvementKey}>
                {primaryImprovement.key}
              </span>
              {primaryImprovement.description && (
                <span className={styles.improvementDescription}>
                  {truncateDescription(primaryImprovement.description)}
                </span>
              )}
            </span>
            {extraCount > 0 && (
              <span className={styles.extraCountPill}>+{extraCount}</span>
            )}
          </div>
        );
      },
      // sortingFn: (rowA, rowB) => {
      //   const a = rowA.original?.linked_improvements?.length > 0 ? 1 : 0;
      //   const b = rowB.original?.linked_improvements?.length > 0 ? 1 : 0;
      //   return a - b;
      // },
      enableSorting: false,
    },
  ];

  const pickFirstField = (object, candidateKeys) => {
    if (!object) return "";
    for (const key of candidateKeys) {
      const value = object[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };

  const getFindingTitle = (finding) =>
    finding?.reference ||
    finding?.title ||
    finding?.name ||
    finding?.key ||
    t("findings.table.notDefined");

  const getFindingSubtitle = (finding) =>
    finding?.description || finding?.summary || finding?.details || "";

  const getStatusClassName = (status) => {
    switch (status) {
      case "Open":
        return styles.statusOpen;
      case "In Progress":
        return styles.statusInProgress;
      case "In Review":
        return styles.statusInReview;
      case "Done":
        return styles.statusDone;
      default:
        return "";
    }
  };

  const allFindingsFilteredData = findingsArray
    .filter((item) => !item.archived)
    .filter((item) => {
      if (allFindingsStatus && item.status !== allFindingsStatus) return false;
      if (
        allFindingsSeverity &&
        (item.risk?.level || "").toLowerCase() !== allFindingsSeverity
      ) {
        return false;
      }

      const q = allFindingsSearchQuery.trim().toLowerCase();
      if (!q) return true;

      return (
        item.key?.toLowerCase().includes(q) ||
        item.reference?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q) ||
        item.risk?.risk_id?.toLowerCase().includes(q) ||
        item.risk?.qms_id?.toLowerCase().includes(q)
      );
    });

    console.log(findingsData, 'findingsData');

  return (
    <MainLayout>
      <div className={styles.column}>
        <BulkEditModal modalType="findings" orgId={orgId} />
        <LinkImprovementModal />
        <FindingsModal />
        {isFindingsDataLoading && (
          <PageLoader text={t("findings.loading")} />
        )}
        {!isFindingsDataLoading && (
          <>
            {/* <Breadcrumbs data={currentModule} /> */}
            <div className={styles.findingsContainer}>
              <div className={styles.pageHeader}>
                <div className={styles.titleGroup}>
                  <div className={styles.titleIcon}>
                    <AlertCircle size={32} />
                  </div>
                  <div className={styles.pageTitle}>
                    {t("modules.findings")}
                  </div>
                </div>
                <div className={styles.pageSubtitle}>
                  Track and resolve compliance findings
                </div>
              </div>

              <div className={styles.tabsRow}>
                <div className={styles.tabSwitcher} role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "overview"}
                    className={classNames(styles.tabButton, {
                      [styles.tabButtonActive]: activeTab === "overview",
                    })}
                    onClick={() => handleTabChange("overview")}
                  >
                    {t("findings.tabs.overview")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "all"}
                    className={classNames(styles.tabButton, {
                      [styles.tabButtonActive]: activeTab === "all",
                    })}
                    onClick={() => handleTabChange("all")}
                  >
                    {t("findings.tabs.allFindings")}
                  </button>
                </div>
              </div>

              {activeTab === "overview" && (
                <>
                  <div className={styles.actionsRow}>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleBulkEditClick}
                      disabled={loading}
                      type="button"
                    >
                      <Edit2 size={16} />
                      {t("findings.actions.bulkEdit")}
                    </button>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleArchiveClick}
                      disabled={loading}
                      type="button"
                    >
                      <Archive size={16} />
                      {loading && loadingAction === "archive"
                        ? t("findings.actions.archiving")
                        : t("findings.actions.archive")}
                    </button>
                    <button
                      className={styles.actionGhostButton}
                      onClick={handleExportClick}
                      disabled={loading}
                      type="button"
                    >
                      <FileText size={16} />
                      {loading && loadingAction === "export"
                        ? t("findings.actions.exporting")
                        : t("findings.actions.export")}
                    </button>
                    <Button
                      text={t("findings.actions.createImprovement")}
                      className={styles.primaryAction}
                      onClick={handleCreateImprovementClick}
                      disabled={loading}
                      icon={<TrendingUp size={16} />}
                      iconPosition="left"
                    />
                  </div>

                  {actionToast && (
                    <div
                      className={styles.bottomToast}
                      role="alert"
                      aria-live="assertive"
                    >
                      <div className={styles.bottomToastTitle}>
                        {actionToast.title}
                      </div>
                      <div className={styles.bottomToastText}>
                        {actionToast.text}
                      </div>
                    </div>
                  )}

                  <div className={styles.filtersRow}>
                    <MultiSelect
                      label={
                        selectedResponsibles.length > 0
                          ? selectedResponsibles.join(", ")
                          : t("findings.filters.responsible")
                      }
                      options={uniqueResponsibles}
                      onChangeSelectedValues={handleResponsibleFilterChange}
                      selectedValues={selectedResponsibles}
                      disabled={!findingsArray.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedStatuses.length > 0
                          ? selectedStatuses.join(", ")
                          : t("findings.filters.status")
                      }
                      options={uniqueStatuses}
                      onChangeSelectedValues={handleStatusFilterChange}
                      selectedValues={selectedStatuses}
                      disabled={!findingsArray.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedLevels.length > 0
                          ? selectedLevels
                            .map(
                              (level) =>
                                uniqueLevels.find(
                                  (option) => option.id === level,
                                )?.label || level,
                            )
                            .join(", ")
                          : t("findings.filters.levels")
                      }
                      options={uniqueLevels}
                      onChangeSelectedValues={handleLevelFilterChange}
                      selectedValues={selectedLevels}
                      disabled={!findingsArray.length}
                      className={styles.multiselect}
                    />
                    <MultiSelect
                      label={
                        selectedRisks.length > 0
                          ? selectedRisks.join(", ")
                          : t("findings.filters.risk")
                      }
                      options={uniqueRisks}
                      onChangeSelectedValues={handleRiskFilterChange}
                      selectedValues={selectedRisks}
                      disabled={!findingsArray.length}
                      className={styles.multiselect}
                    />
                    <DateRangeFilter
                      handleDateRangeChange={handleDateRangeFilterChange}
                      dateRange={dateRange}
                      isClearable
                      className={styles.findingDateFilter}
                    />
                    <Input
                      type="text"
                      placeholder={t("findings.filters.search")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.input}
                      icon={SearchIcon}
                      wrapperClassName={styles.searchWrapper}
                    />
                  </div>

                  {hasSelectedFindings && (
                    <div className={styles.selectionBar}>
                      <div className={styles.selectionMeta}>
                        <span className={styles.selectionCount}>
                          {selectedFindingsLabel}
                        </span>
                        <button
                          type="button"
                          className={styles.selectionClearButton}
                          onClick={handleClearSelection}
                        >
                          {t("findings.selectionBar.clear")}
                        </button>
                      </div>
                      <Button
                        text={t("findings.actions.createImprovement")}
                        className={classNames(
                          styles.primaryAction,
                          styles.selectionCreateButton,
                        )}
                        onClick={handleCreateImprovementClick}
                        disabled={loading}
                        icon={<TrendingUp size={16} />}
                        iconPosition="left"
                      />
                    </div>
                  )}

                  <div
                    className={classNames(
                      styles.tableCard,
                      !filteredData.length && styles.tableCardEmpty,
                    )}
                  >
                    <Table
                      columns={columns}
                      data={filteredData}
                      onRowClick={handleRowClick}
                      onSelectedRowsChange={handleSelectedRowsChange}
                      rowHover
                      initialSorting={[{ id: "date", desc: true }]}
                      resetSelection={resetRowSelection}
                      centered
                      variant="findings"
                    />
                    {!filteredData.length && (
                      <div className={styles.tableEmptyState}>
                        <div className={styles.tableEmptyContent}>
                          <AlertCircle size={48} className={styles.tableEmptyIcon} />
                          <div className={styles.tableEmptyCopy}>
                            {t("findings.emptyStateTitle")}
                          </div>
                          <Button
                            text={t("findings.emptyStateCta")}
                            className={styles.tableEmptyCta}
                            onClick={() =>
                              dispatch(showModal({ name: "new_finding_modal" }))
                            }
                            icon={Plus}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === "all" && (
                <>
                  <div className={styles.allFindingsFiltersRow}>
                    <Input
                      type="text"
                      placeholder={t("findings.filters.searchFindings")}
                      value={allFindingsSearchQuery}
                      onChange={(e) =>
                        setAllFindingsSearchQuery(e.target.value)
                      }
                      className={styles.input}
                      icon={SearchIcon}
                      wrapperClassName={styles.allFindingsSearchWrapper}
                    />

                    <Select
                      text={t("findings.filters.severity")}
                      value={allFindingsSeverity}
                      onChange={(nextValue) =>
                        setAllFindingsSeverity(nextValue || "")
                      }
                      options={[
                        { id: "", label: t("findings.filters.severity") },
                        ...uniqueLevels,
                      ]}
                      disabled={!findingsArray.length}
                      className={styles.selectControl}
                      variant="filters"
                    />

                    <Select
                      text={t("findings.filters.status")}
                      value={allFindingsStatus}
                      onChange={(nextValue) =>
                        setAllFindingsStatus(nextValue || "")
                      }
                      options={[
                        { id: "", label: t("findings.filters.status") },
                        ...uniqueStatuses,
                      ]}
                      disabled={!findingsArray.length}
                      className={styles.selectControl}
                      variant="filters"
                    />
                  </div>

                  {!allFindingsFilteredData.length && (
                    <div className={styles.cardsEmptyState}>
                      <AlertCircle size={40} />
                      <div className={styles.emptyCopy}>
                        {t("findings.noFindings")}
                      </div>
                    </div>
                  )}

                  <div className={styles.findingsCardsList}>
                    {allFindingsFilteredData.map((finding) => {
                      const statusClassName = getStatusClassName(
                        finding.status,
                      );

                      const rootCause = pickFirstField(finding, [
                        "root_cause",
                        "rootCause",
                        "rootCauseDescription",
                        "rootCauseText",
                        "cause",
                      ]);
                      const correctiveAction = pickFirstField(finding, [
                        "corrective_action",
                        "correctiveAction",
                        "action_plan",
                        "actionPlan",
                        "corrective",
                        "action",
                      ]);
                      const hasRootCause = Boolean(rootCause);
                      const hasCorrectiveAction = Boolean(correctiveAction);
                      const hasDetails = hasRootCause || hasCorrectiveAction;

                      return (
                        <div
                          key={finding.id || finding.key}
                          className={styles.findingCard}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleFindingCardClick(finding)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleFindingCardClick(finding);
                            }
                          }}
                        >
                          <div className={styles.findingCardHeader}>
                            <div className={styles.findingCardTitleRow}>
                              <span className={styles.keyPill}>
                                {finding.key}
                              </span>
                              <div className={styles.findingCardTitle}>
                                {getFindingTitle(finding)}
                              </div>
                            </div>

                            <div className={styles.findingCardMeta}>
                              {finding.status && (
                                <span
                                  className={classNames(
                                    styles.statusBadge,
                                    statusClassName,
                                  )}
                                >
                                  {finding.status}
                                </span>
                              )}
                            </div>
                          </div>

                          {getFindingSubtitle(finding) && (
                            <div className={styles.findingCardSubtitle}>
                              {getFindingSubtitle(finding)}
                            </div>
                          )}

                          {hasDetails && (
                            <div className={styles.findingCardDetails}>
                              {hasRootCause && (
                                <div className={styles.detailBlock}>
                                  <div className={styles.detailLabel}>
                                    {t("findings.allFindings.rootCause")}
                                  </div>
                                  <div className={styles.detailValue}>
                                    {rootCause}
                                  </div>
                                </div>
                              )}
                              {hasCorrectiveAction && (
                                <div className={styles.detailBlock}>
                                  <div className={styles.detailLabel}>
                                    {t("findings.allFindings.correctiveAction")}
                                  </div>
                                  <div className={styles.detailValue}>
                                    {correctiveAction}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* <Separator className={styles.separator} />
              <div className={styles.chartWrapper}>
                <img
                  src={pieChartUrl}
                  alt={t("findings.charts.riskLevelsTitle")}
                  className={styles.chartImage}
                />
                <img
                  src={barChartUrl}
                  alt={t("findings.charts.qmsChaptersTitle")}
                  className={styles.chartImage}
                />
              </div> */}
            </div>
          </>
        )}
      </div>
      {showConfirmationPopup && (
        <ConfirmationPopup
          message={t("findings.confirmation.archiveMessage")}
          illustration={ArchiveIllustration}
          confirmText={t("findings.confirmation.archiveConfirm")}
          onConfirm={handleBulkArchive}
          onCancel={() => setShowConfirmationPopup(false)}
        />
      )}
    </MainLayout>
  );
};

export default Findings;
